import React, { useState, useEffect } from 'react';
import {
  Clock, Plus, Trash2, Calendar, DollarSign,
  Loader2, AlertCircle, FileText
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const CollaborationTimePanel = ({ collaborationId, isExpert }) => {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({ total_hours: 0, total_cost: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    hours: '',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (collaborationId) {
      loadTimeEntries();
    }
  }, [collaborationId]);

  const loadTimeEntries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/collaborations/${collaborationId}/time`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      setEntries(response.data.entries || []);
      setSummary(response.data.summary || { total_hours: 0, total_cost: 0 });
    } catch (error) {
      console.error('Failed to load time entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.hours || !formData.description) {
      toast.error('Vennligst fyll ut alle felt');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/collaborations/${collaborationId}/time`,
        {
          hours: parseFloat(formData.hours),
          description: formData.description,
          date: formData.date
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success('Time logget!');
      setFormData({
        hours: '',
        description: '',
        date: new Date().toISOString().split('T')[0]
      });
      setShowForm(false);
      loadTimeEntries(); // Reload
    } catch (error) {
      console.error('Failed to log time:', error);
      toast.error('Kunne ikke logge time');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BACKEND_URL}/api/collaborations/time/${entryId}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success('Time slettet');
      loadTimeEntries();
    } catch (error) {
      console.error('Failed to delete entry:', error);
      toast.error('Kunne ikke slette');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Timeføring
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {isExpert ? 'Logg timer du har jobbet på denne saken' : 'Oversikt over ekspertens timer'}
          </p>
        </div>
        
        {isExpert && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
          >
            <Plus className="w-4 h-4" />
            Logg time
          </button>
        )}
      </div>

      {/* Time Entry Form */}
      {showForm && isExpert && (
        <form onSubmit={handleSubmit} className="bg-blue-50 rounded-xl border border-blue-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Timer
              </label>
              <input
                type="number"
                step="0.25"
                min="0.25"
                value={formData.hours}
                onChange={(e) => setFormData({...formData, hours: e.target.value})}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="2.5"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dato
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div className="md:col-span-1 flex items-end">
              <button
                type="submit"
                disabled={saving}
                className="w-full px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Lagrer...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Legg til
                  </>
                )}
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Beskrivelse
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
              rows={2}
              placeholder="Hva jobbet du med?"
              required
            />
          </div>
        </form>
      )}

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-4">
          <div className="flex items-center gap-2 text-blue-700 mb-1">
            <Clock className="w-4 h-4" />
            <span className="text-sm font-medium">Total timer</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{summary.total_hours}t</p>
        </div>
        
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 p-4">
          <div className="flex items-center gap-2 text-green-700 mb-1">
            <DollarSign className="w-4 h-4" />
            <span className="text-sm font-medium">Total kostnad</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {summary.total_cost.toLocaleString('no-NO')} kr
          </p>
        </div>
        
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200 p-4">
          <div className="flex items-center gap-2 text-purple-700 mb-1">
            <FileText className="w-4 h-4" />
            <span className="text-sm font-medium">Oppføringer</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{entries.length}</p>
        </div>
      </div>

      {/* Time Entries */}
      <div className="space-y-3">
        {entries.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
            <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600">Ingen timer logget ennå</p>
          </div>
        ) : (
          entries.map(entry => (
            <div
              key={entry.id}
              className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full">
                      {entry.hours}t
                    </span>
                    <span className="text-sm text-gray-500">
                      {new Date(entry.date).toLocaleDateString('no-NO')}
                    </span>
                    {entry.status && (
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        entry.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                        entry.status === 'APPROVED' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {entry.status}
                      </span>
                    )}
                  </div>
                  
                  <p className="text-gray-900">{entry.description}</p>
                  
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                    <span>{entry.expert_name}</span>
                    {entry.total_cost > 0 && (
                      <>
                        <span>•</span>
                        <span className="font-medium">
                          {entry.total_cost.toLocaleString('no-NO')} kr
                        </span>
                        <span className="text-xs">
                          ({entry.hourly_rate} kr/t)
                        </span>
                      </>
                    )}
                  </div>
                </div>
                
                {isExpert && entry.status === 'PENDING' && (
                  <button
                    onClick={() => handleDelete(entry.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                    title="Slett"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CollaborationTimePanel;
